/**
 * GovernanceCountColumns - R/I/D/Dep count columns for Work Items Grid
 * Shows linked governance artifact counts with hover popovers
 * Feature flag: features.governance.relationships
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AlertTriangle, AlertCircle, GitBranch, Link2, ExternalLink, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';

// Cache for governance counts (workId -> { data, timestamp })
const governanceCache = new Map();
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes
const HOVER_DELAY = 200; // ms before showing popover

// Severity/priority colors
const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  blocked: 'bg-red-100 text-red-700'
};

// Get cached data or return null
const getCached = (workId) => {
  const cached = governanceCache.get(workId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

// Set cache
const setCache = (workId, data) => {
  governanceCache.set(workId, { data, timestamp: Date.now() });
};

// Popover component for showing linked items
const GovernancePopover = ({ 
  items, 
  type, 
  workId, 
  onOpenInspector, 
  onClose,
  position 
}) => {
  const typeLabels = {
    risks: 'Risk',
    issues: 'Issue', 
    decisions: 'Decision',
    dependencies: 'Dependency'
  };
  
  const TypeIcon = {
    risks: AlertTriangle,
    issues: AlertCircle,
    decisions: GitBranch,
    dependencies: Link2
  }[type] || Link2;
  
  const getItemBadge = (item) => {
    if (type === 'risks' && item.severity) {
      return (
        <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${SEVERITY_COLORS[item.severity?.toLowerCase()] || SEVERITY_COLORS.medium}`}>
          {item.severity}
        </span>
      );
    }
    if (type === 'issues' && item.priority) {
      return (
        <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${SEVERITY_COLORS[item.priority?.toLowerCase()] || SEVERITY_COLORS.medium}`}>
          {item.priority}
        </span>
      );
    }
    if (type === 'decisions' && item.status) {
      return (
        <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${STATUS_COLORS[item.status?.toLowerCase()] || STATUS_COLORS.pending}`}>
          {item.status}
        </span>
      );
    }
    return null;
  };
  
  const displayItems = items.slice(0, 3);
  const moreCount = items.length - 3;
  
  return (
    <div 
      className="absolute z-50 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3"
      style={{ 
        top: position?.top || '100%', 
        left: position?.left || '50%',
        transform: 'translateX(-50%)'
      }}
      onMouseLeave={onClose}
      data-testid={`gov-popover-${type}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
        <TypeIcon className="w-4 h-4 text-gray-500" />
        <span className="text-xs font-medium text-gray-700">
          {items.length} {typeLabels[type]}{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      {/* Items list */}
      <div className="space-y-1.5 mb-3">
        {displayItems.map((item, idx) => (
          <div key={item.id || idx} className="flex items-center gap-2">
            {getItemBadge(item)}
            <span className="text-xs text-gray-800 truncate flex-1" title={item.title}>
              {item.title}
            </span>
          </div>
        ))}
        {moreCount > 0 && (
          <div className="text-[10px] text-gray-500 italic">
            … +{moreCount} more
          </div>
        )}
      </div>
      
      {/* CTA */}
      <button
        onClick={() => {
          trackEvent(TELEMETRY_EVENTS.GOV_GRID_OPEN_IN_INSPECTOR, { workId, tab: type });
          onOpenInspector?.(workId, type);
        }}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        data-testid="open-in-inspector-btn"
      >
        <ExternalLink className="w-3 h-3" />
        Open in Inspector
      </button>
    </div>
  );
};

// Single count cell component
export const GovernanceCountCell = ({ 
  workId, 
  type, 
  hasPermission = true,
  onOpenInspector,
  className = ''
}) => {
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [count, setCount] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [error, setError] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const cellRef = useRef(null);
  
  // Check feature flag
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  // Load governance data
  const loadData = useCallback(async () => {
    if (!workId || !tenantId || !featureEnabled) return;
    
    // Check cache first
    const cached = getCached(workId);
    if (cached) {
      const typeKey = type === 'dependencies' ? 'dependencies' : type;
      setItems(cached[typeKey] || []);
      setCount(cached[typeKey]?.length || 0);
      return;
    }
    
    setLoading(true);
    setError(false);
    
    try {
      const response = await api.getGovernanceByWork(workId, tenantId, false);
      const data = response.data;
      
      // Cache the response
      setCache(workId, data);
      
      const typeKey = type === 'dependencies' ? 'dependencies' : type;
      setItems(data[typeKey] || []);
      setCount(data[typeKey]?.length || 0);
    } catch (err) {
      console.error('Failed to load governance data:', err);
      setError(true);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [workId, tenantId, type, featureEnabled]);
  
  // Load on mount if cached
  useEffect(() => {
    if (featureEnabled && workId && tenantId) {
      const cached = getCached(workId);
      if (cached) {
        const typeKey = type === 'dependencies' ? 'dependencies' : type;
        setItems(cached[typeKey] || []);
        setCount(cached[typeKey]?.length || 0);
      }
    }
  }, [workId, tenantId, type, featureEnabled]);
  
  // Handle hover - load data on hover with delay
  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(async () => {
      await loadData();
      // After loading, check if we have items to show
      const cached = getCached(workId);
      const typeKey = type === 'dependencies' ? 'dependencies' : type;
      const cachedItems = cached?.[typeKey] || [];
      
      if (cachedItems.length > 0) {
        setShowPopover(true);
        trackEvent(TELEMETRY_EVENTS.GOV_GRID_POPOVER_OPENED, { workId, tab: type });
      }
    }, HOVER_DELAY);
  };
  
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowPopover(false);
  };
  
  if (!featureEnabled) {
    return null;
  }
  
  if (!hasPermission) {
    return (
      <div className={`text-center text-gray-400 ${className}`}>—</div>
    );
  }
  
  const TypeIcon = {
    risks: AlertTriangle,
    issues: AlertCircle,
    decisions: GitBranch,
    dependencies: Link2
  }[type] || Link2;
  
  const iconColors = {
    risks: 'text-red-500',
    issues: 'text-orange-500',
    decisions: 'text-blue-500',
    dependencies: 'text-purple-500'
  };
  
  return (
    <div 
      ref={cellRef}
      className={`relative inline-flex items-center justify-center ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`gov-count-${type}-${workId}`}
    >
      {loading ? (
        <RefreshCw className="w-3 h-3 text-gray-400 animate-spin" />
      ) : error ? (
        <span className="text-xs text-gray-400">-</span>
      ) : count === null ? (
        <span className="text-xs text-gray-400">…</span>
      ) : (
        <span className={`text-xs font-medium ${count > 0 ? iconColors[type] : 'text-gray-400'}`}>
          {count}
        </span>
      )}
      
      {showPopover && items.length > 0 && (
        <GovernancePopover
          items={items}
          type={type}
          workId={workId}
          onOpenInspector={onOpenInspector}
          onClose={() => setShowPopover(false)}
          position={{ top: '24px', left: '50%' }}
        />
      )}
    </div>
  );
};

// Column header component
export const GovernanceColumnHeader = ({ type, onSort, sortDirection }) => {
  const labels = {
    risks: 'R',
    issues: 'I',
    decisions: 'D',
    dependencies: 'Dep'
  };
  
  const tooltips = {
    risks: 'Linked Risks',
    issues: 'Linked Issues',
    decisions: 'Linked Decisions',
    dependencies: 'Linked Dependencies'
  };
  
  const TypeIcon = {
    risks: AlertTriangle,
    issues: AlertCircle,
    decisions: GitBranch,
    dependencies: Link2
  }[type] || Link2;
  
  const iconColors = {
    risks: 'text-red-500',
    issues: 'text-orange-500',
    decisions: 'text-blue-500',
    dependencies: 'text-purple-500'
  };
  
  return (
    <button
      onClick={() => onSort?.(type)}
      className="flex items-center justify-center gap-0.5 px-1 py-1 hover:bg-gray-100 rounded transition-colors"
      title={tooltips[type]}
      data-testid={`gov-col-header-${type}`}
    >
      <TypeIcon className={`w-3 h-3 ${iconColors[type]}`} />
      <span className="text-[10px] font-medium text-gray-600 hidden sm:inline">
        {labels[type]}
      </span>
      {sortDirection && (
        <span className="text-[8px] text-gray-400">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );
};

// Hook for loading governance counts for multiple work items
export const useGovernanceCounts = (workIds, tenantId, enabled = true) => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!enabled || !workIds?.length || !tenantId) return;
    
    const loadCounts = async () => {
      setLoading(true);
      const newCounts = {};
      
      // Load in batches of 10
      const batches = [];
      for (let i = 0; i < workIds.length; i += 10) {
        batches.push(workIds.slice(i, i + 10));
      }
      
      for (const batch of batches) {
        await Promise.all(batch.map(async (workId) => {
          // Check cache first
          const cached = getCached(workId);
          if (cached) {
            newCounts[workId] = {
              risks: cached.risks?.length || 0,
              issues: cached.issues?.length || 0,
              decisions: cached.decisions?.length || 0,
              dependencies: cached.dependencies?.length || 0
            };
            return;
          }
          
          try {
            const response = await api.getGovernanceByWork(workId, tenantId, false);
            const data = response.data;
            setCache(workId, data);
            
            newCounts[workId] = {
              risks: data.risks?.length || 0,
              issues: data.issues?.length || 0,
              decisions: data.decisions?.length || 0,
              dependencies: data.dependencies?.length || 0
            };
          } catch (err) {
            newCounts[workId] = { risks: 0, issues: 0, decisions: 0, dependencies: 0 };
          }
        }));
      }
      
      setCounts(newCounts);
      setLoading(false);
    };
    
    loadCounts();
  }, [workIds?.join(','), tenantId, enabled]);
  
  return { counts, loading };
};

// Export cache clearing function for refreshes
export const clearGovernanceCache = (workId = null) => {
  if (workId) {
    governanceCache.delete(workId);
  } else {
    governanceCache.clear();
  }
};

/**
 * GovernanceColumnsWrapper - Renders all 4 governance columns for a row
 * Used in HierarchyTable to add governance indicators
 */
export const GovernanceColumnsWrapper = ({ 
  workId, 
  tenantId,
  onOpenInspector,
  hasPermission = true,
  className = '' 
}) => {
  const { getFeature } = useProductMode();
  
  // Check feature flag
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  if (!featureEnabled) {
    return null;
  }
  
  return (
    <>
      <td className={`py-2 px-1 text-center ${className}`}>
        <GovernanceCountCell 
          workId={workId} 
          type="risks" 
          hasPermission={hasPermission}
          onOpenInspector={onOpenInspector}
        />
      </td>
      <td className={`py-2 px-1 text-center ${className}`}>
        <GovernanceCountCell 
          workId={workId} 
          type="issues" 
          hasPermission={hasPermission}
          onOpenInspector={onOpenInspector}
        />
      </td>
      <td className={`py-2 px-1 text-center ${className}`}>
        <GovernanceCountCell 
          workId={workId} 
          type="decisions" 
          hasPermission={hasPermission}
          onOpenInspector={onOpenInspector}
        />
      </td>
      <td className={`py-2 px-1 text-center ${className}`}>
        <GovernanceCountCell 
          workId={workId} 
          type="dependencies" 
          hasPermission={hasPermission}
          onOpenInspector={onOpenInspector}
        />
      </td>
    </>
  );
};

/**
 * GovernanceColumnHeaders - Renders header cells for governance columns
 */
export const GovernanceColumnHeaders = ({ 
  onSort, 
  sortColumn, 
  sortDirection,
  className = ''
}) => {
  const { getFeature } = useProductMode();
  
  // Check feature flag
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  if (!featureEnabled) {
    return null;
  }
  
  const types = ['risks', 'issues', 'decisions', 'dependencies'];
  
  return (
    <>
      {types.map(type => (
        <th 
          key={type}
          className={`py-3 px-1 text-center text-xs font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider w-12 hidden lg:table-cell ${className}`}
        >
          <GovernanceColumnHeader
            type={type}
            onSort={() => onSort?.(type)}
            sortDirection={sortColumn === type ? sortDirection : null}
          />
        </th>
      ))}
    </>
  );
};

/**
 * RoadmapRiskIndicator - Warning dot for roadmap bars with high-severity risks
 * Shows on hover with tooltip containing risk details
 */
export const RoadmapRiskIndicator = ({ 
  workId, 
  tenantId,
  onOpenInspector,
  position = { right: 4, top: '50%' }
}) => {
  const { getFeature } = useProductMode();
  const [highRisks, setHighRisks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);
  const indicatorRef = useRef(null);
  
  // Check feature flag
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  // Load high-severity risks
  const loadRisks = useCallback(async () => {
    if (!workId || !tenantId || !featureEnabled) return;
    
    // Check cache first
    const cached = getCached(workId);
    if (cached) {
      const high = (cached.risks || []).filter(r => 
        r.severity?.toLowerCase() === 'high' || r.severity?.toLowerCase() === 'critical'
      );
      setHighRisks(high);
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.getGovernanceByWork(workId, tenantId, false);
      const data = response.data;
      setCache(workId, data);
      
      const high = (data.risks || []).filter(r => 
        r.severity?.toLowerCase() === 'high' || r.severity?.toLowerCase() === 'critical'
      );
      setHighRisks(high);
    } catch (err) {
      console.error('Failed to load risks for roadmap:', err);
      setHighRisks([]);
    } finally {
      setLoading(false);
    }
  }, [workId, tenantId, featureEnabled]);
  
  // Pre-load on mount if cached
  useEffect(() => {
    if (featureEnabled && workId && tenantId) {
      const cached = getCached(workId);
      if (cached) {
        const high = (cached.risks || []).filter(r => 
          r.severity?.toLowerCase() === 'high' || r.severity?.toLowerCase() === 'critical'
        );
        setHighRisks(high);
      }
    }
  }, [workId, tenantId, featureEnabled]);
  
  // Handle hover
  const handleMouseEnter = (e) => {
    hoverTimeoutRef.current = setTimeout(async () => {
      await loadRisks();
      if (indicatorRef.current) {
        const rect = indicatorRef.current.getBoundingClientRect();
        setTooltipPos({ x: rect.left, y: rect.bottom + 4 });
      }
      setShowTooltip(true);
      
      // Track telemetry
      if (highRisks.length > 0) {
        trackEvent(TELEMETRY_EVENTS.GOV_ROADMAP_WARNING_HOVER, { 
          workId, 
          highCount: highRisks.length 
        });
      }
    }, 150);
  };
  
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowTooltip(false);
  };
  
  const handleClick = () => {
    trackEvent(TELEMETRY_EVENTS.GOV_ROADMAP_OPEN_IN_INSPECTOR, { workId });
    onOpenInspector?.(workId, 'risks');
  };
  
  if (!featureEnabled || (highRisks.length === 0 && !loading)) {
    return null;
  }
  
  return (
    <>
      {/* Warning Dot */}
      <div
        ref={indicatorRef}
        className="absolute cursor-pointer z-[6]"
        style={{
          right: position.right,
          top: position.top,
          transform: 'translateY(-50%)'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        data-testid={`roadmap-risk-indicator-${workId}`}
      >
        <div 
          className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-sm animate-pulse"
          title={`${highRisks.length} high-severity risk${highRisks.length !== 1 ? 's' : ''}`}
        />
      </div>
      
      {/* Tooltip */}
      {showTooltip && highRisks.length > 0 && (
        <div
          className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[200px] max-w-[280px]"
          style={{ top: tooltipPos.y, left: tooltipPos.x, transform: 'translateX(-50%)' }}
        >
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium text-gray-700">
              {highRisks.length} High-Severity Risk{highRisks.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="space-y-1.5 mb-3">
            {highRisks.slice(0, 2).map((risk, idx) => (
              <div key={risk.id || idx} className="flex items-center gap-2">
                <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${
                  risk.severity?.toLowerCase() === 'critical' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {risk.severity}
                </span>
                <span className="text-xs text-gray-800 truncate flex-1" title={risk.title}>
                  {risk.title}
                </span>
              </div>
            ))}
            {highRisks.length > 2 && (
              <div className="text-[10px] text-gray-500 italic">
                … +{highRisks.length - 2} more
              </div>
            )}
          </div>
          
          <button
            onClick={handleClick}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Open in Inspector
          </button>
        </div>
      )}
    </>
  );
};

export default GovernanceCountCell;
